import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@mui/material";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

// compact: a smaller trigger for tight spots (tiles, toolbars). menuMinWidth: the menu can be wider than a narrow trigger.
const FormDropdown = ({ id, value, onChange, placeholder, options, disabled, action, multiple, searchable = false, compact = false, menuMinWidth = 0 }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : null;
  const selected = multiple
    ? options.filter((option) => selectedValues.includes(option.value))
    : options.find((option) => option.value === value);
  const closedLabel = (option) => option.valueLabel || option.label;
  const selectedLabel = multiple
    ? selected.map(closedLabel).join(", ")
    : closedLabel(selected || {});
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = searchable && normalizedQuery
    ? options.filter((option) => (option.label || "").toLowerCase().includes(normalizedQuery))
    : options;

  const placeMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 240 && rect.top > spaceBelow;
    const width = Math.max(rect.width, menuMinWidth);
    setMenuStyle({
      position: "fixed",
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      width,
      zIndex: 80,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (open) return undefined;
    setQuery("");
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (searchable) searchRef.current?.focus();
    placeMenu();
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open]);

  const borderColor = theme.palette.secondary.v4;
  const hasBadges = multiple && selected.length > 0;
  const removeSelected = (optionValue) => {
    onChange(selectedValues.filter((item) => item !== optionValue));
  };

  return (
    <div>
      <div
        id={id}
        ref={buttonRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          if (!disabled) setOpen((current) => !current);
        }}
        className={`w-full ${compact ? "px-2 py-1 text-xs" : "px-3 py-2"} border rounded-md bg-white text-left flex items-center gap-2 ${disabled ? "opacity-60" : ""}`}
        style={{ borderColor, color: "#111111" }}
      >
        {hasBadges ? (
          <span className="flex min-w-0 flex-1 flex-nowrap gap-1.5 overflow-x-auto">
            {selected.map((option) => {
              const name = closedLabel(option);
              return (
                <span
                  key={option.value}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
                  style={{ backgroundColor: theme.palette.info.main }}
                >
                  <span className="whitespace-nowrap">{name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    disabled={disabled}
                    className="inline-flex shrink-0"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!disabled) removeSelected(option.value);
                    }}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
          </span>
        ) : (
          <span className={`min-w-0 flex-1 truncate ${selectedLabel ? "" : "text-gray-500"}`}>
            {selectedLabel || placeholder}
          </span>
        )}
        {hasBadges && (
          <button
            type="button"
            aria-label="Clear selected"
            disabled={disabled}
            className="inline-flex shrink-0 text-gray-500"
            onClick={(event) => {
              event.stopPropagation();
              if (!disabled) onChange([]);
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <ChevronDownIcon className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`} aria-hidden="true" />
      </div>
      {open && menuStyle && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          aria-labelledby={id}
          aria-multiselectable={multiple || undefined}
          // No top padding when searchable: the sticky search box would stick below it and
          // options would show through the gap
          className={`max-h-60 overflow-auto rounded-md border bg-white shadow-lg ${searchable ? "pb-1" : "py-1"}`}
          style={{ ...menuStyle, borderColor }}
        >
          {searchable && (
            <li className="sticky top-0 z-10 mb-1 border-b bg-white px-2 py-2" style={{ borderColor }}>
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                aria-label="Search"
                className="w-full rounded-md border px-2 py-1.5 text-sm"
                style={{ borderColor, color: "#111111" }}
              />
            </li>
          )}
          {visibleOptions.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">{options.length === 0 ? "No options" : "No matches"}</li>
          )}
          {visibleOptions.map((option) => {
            const isSelected = multiple
              ? selectedValues.includes(option.value)
              : option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
                  style={{
                    color: "#111111",
                    backgroundColor: isSelected ? "#E8EEF8" : "#ffffff",
                  }}
                  onClick={() => {
                    if (option.disabled) return;
                    if (multiple) {
                      onChange(
                        isSelected
                          ? selectedValues.filter((item) => item !== option.value)
                          : [...selectedValues, option.value]
                      );
                      return;
                    }
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      readOnly
                      checked={isSelected}
                      tabIndex={-1}
                      aria-hidden="true"
                      className="pointer-events-none"
                    />
                  )}
                  <span>{option.label}</span>
                </button>
              </li>
            );
          })}
          {action && (
            <li className="border-t" style={{ borderColor }}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-gray-100"
                style={{ color: theme.palette.info.main }}
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
              >
                {action.label}
              </button>
            </li>
          )}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default FormDropdown;
